// Pure helpers (they only import the projection helpers) for the EXACT values of
// the rules, computed on example JSON documents kept in memory.
//
// The flat values of Keys and Values ("titles.text" -> all the texts) have no
// correlation between the fields of an element, so with the filter
// "titles[type=main]", or the rule key "titles[type=main].text", they offer
// values that the rule never sees. With the documents the values are computed
// as the pipeline does, in the same order:
//   1. the projection (Include and Protect) and its filters, as applyProjection
//      of ares-pipeline: the filters are checked on the SOURCE element and remove
//      whole elements (null and non-objects too); `{}` for the elements without
//      selected fields; primitives where an object is expected are left out;
//   2. the selectors of the key of the rule, as getDeep of ares-pipeline
//      (matchesFilters: "=" and "a|b" need one of the values, "!=" keeps an
//      element that has not the field, null and undefined never match, values are
//      compared as strings; on a single object the selector keeps or drops it);
//   3. the values of the leaf, as the synthesizer reads them (arrays one level
//      deep, null and undefined skipped, each value as a string).
// The transformer is applied after, by the caller. Without an active projection
// that has an Include only the step 2 and 3 apply.

import {
  activeRuleKeys,
  parseFilterSegments,
  projectionView,
  validateFilter
} from "./projection";

// What is kept in memory for a session of the editor
export const MAX_DOCUMENTS = 300;
export const MAX_CHARS = 30000000; // of JSON text (about 30 MB)

const isObject = (value) => value !== null && typeof value === "object";
const isPlainObject = (value) => isObject(value) && !Array.isArray(value);
// The documents come from JSON.parse: a JSON round trip is a deep copy
const cloneValue = (value) => (isObject(value) ? JSON.parse(JSON.stringify(value)) : value);

// matchesFilters of ares-pipeline
function matches(element, selectors) {
  if (selectors.length === 0) return true;
  if (element === null || typeof element !== "object") return false;

  return selectors.every(({ field, values, negate }) => {
    const value = field
      .split(".")
      .reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), element);
    const candidates = Array.isArray(value) ? value : [value];
    const hit = candidates.some(
      (candidate) => candidate !== undefined && candidate !== null && values.includes(String(candidate))
    );

    return negate ? !hit : hit;
  });
}

// ---------------------------------------------------------------------------
// The projection of a document (applyProjection of ares-pipeline)
// ---------------------------------------------------------------------------

const OMIT = Symbol("omit");

const keptNode = () => ({ leaf: false, children: new Map() });
const filterNode = () => ({ filters: [], children: new Map() });

// The tree of the kept paths and the tree of the filters. What the backend
// refuses (selectors in a path, a filter that is not valid or on a list that is
// not kept) is left out here: the save is blocked for those.
function compilePlan(projection) {
  const tree = keptNode();

  for (const path of [...(projection.protect ?? []), ...(projection.include ?? [])]) {
    if (typeof path !== "string") continue;

    const { segments } = parseFilterSegments(path.trim());
    if (!segments || segments.some(({ key, selectors }) => !key || selectors.length > 0)) continue;

    let node = tree;
    for (const { key } of segments) {
      if (!node.children.has(key)) node.children.set(key, keptNode());
      node = node.children.get(key);
    }
    node.leaf = true; // a leaf wins over the longer paths that start with it
  }

  let filters;
  for (const filter of Array.isArray(projection.filters) ? projection.filters : []) {
    if (validateFilter(filter)) continue;

    const { segments } = parseFilterSegments(filter);

    // the list must be kept: a node of the tree, or a place below a leaf
    let node = tree;
    let kept = true;
    for (const { key } of segments) {
      if (node.leaf) break;
      node = node.children.get(key);
      if (!node) {
        kept = false;
        break;
      }
    }
    if (!kept) continue;

    filters ??= filterNode();
    let target = filters;
    for (const { key } of segments) {
      if (!target.children.has(key)) target.children.set(key, filterNode());
      target = target.children.get(key);
    }
    target.filters.push(...segments[segments.length - 1].selectors);
  }

  return { tree, filters };
}

// The elements that satisfy every selector; null and non-objects cannot, so they are removed
const filterList = (list, selectors) =>
  list.filter((element) => isPlainObject(element) && matches(element, selectors));

// The filters below a leaf, applied to a COPY (it is modified)
function filterCopy(value, node) {
  const list =
    Array.isArray(value) && node.filters.length > 0 ? filterList(value, node.filters) : value;

  if (node.children.size > 0) {
    for (const element of Array.isArray(list) ? list : [list]) {
      if (!isPlainObject(element)) continue;
      for (const [key, child] of node.children) {
        if (Object.prototype.hasOwnProperty.call(element, key)) {
          element[key] = filterCopy(element[key], child);
        }
      }
    }
  }

  return list;
}

function projectValue(value, node, filter) {
  if (node.leaf) return filter ? filterCopy(cloneValue(value), filter) : cloneValue(value);
  if (value === null) return null;
  if (Array.isArray(value)) return projectArray(value, node, filter);
  if (isPlainObject(value)) return projectObject(value, node, filter);
  return OMIT; // a primitive where an object was expected
}

function projectArray(list, node, filter) {
  // the elements that do not satisfy the filters go first, on the source elements
  if (filter?.filters.length) list = filterList(list, filter.filters);
  // an array of primitives where objects were expected is not copied
  if (!list.some(isPlainObject) && list.some((element) => element !== null)) return OMIT;

  return list.map((element) =>
    element === null ? null : isPlainObject(element) ? projectObject(element, node, filter) : {}
  );
}

function projectObject(source, node, filter) {
  const projected = {};

  for (const key of Object.keys(source)) {
    const child = node.children.get(key);
    if (!child) continue;

    const value = projectValue(source[key], child, filter?.children.get(key));
    if (value !== OMIT) projected[key] = value;
  }

  return projected;
}

/**
 * A document after the projection and its filters, as ares-pipeline gives it to
 * the transformer and the synthesizer.
 * @param {object} doc - A document.
 * @param {{include?: string[], protect?: string[], filters?: string[]}} projection - An active projection with an Include.
 * @returns {object} The projected document (a new one).
 */
export function projectDocument(doc, projection) {
  return applyPlan(doc, compilePlan(projection));
}

const applyPlan = (doc, { tree, filters }) =>
  isPlainObject(doc) ? projectObject(doc, tree, filters) : {};

// ---------------------------------------------------------------------------
// The values of a key (getDeep of ares-pipeline, then the synthesizer)
// ---------------------------------------------------------------------------

// selectValue of ares-pipeline: a selector filters an array, keeps or drops a single object
function select(value, selectors) {
  if (selectors.length === 0) return value;
  if (Array.isArray(value)) return value.filter((element) => matches(element, selectors));
  return matches(value, selectors) ? value : undefined;
}

// collectSegments of ares-pipeline
function collect(obj, segments, index, found) {
  const segment = segments[index];
  if (!segment?.key || !isObject(obj)) return;

  const value = select(obj[segment.key], segment.selectors);

  if (index === segments.length - 1) {
    if (value === undefined) return;
    if (segment.selectors.length > 0 && Array.isArray(value) && value.length === 0) return;
    found.add(value);
    return;
  }

  if (Array.isArray(value)) value.forEach((element) => collect(element, segments, index + 1, found));
  else collect(value, segments, index + 1, found);
}

// getDeep gives one value, or the array of the values; the synthesizer reads an
// array one level deep and counts each value as a string (null and undefined
// are skipped; an array among several values becomes the string "a,b", as it
// does at runtime). Objects are not values.
function leafValues(doc, segments) {
  const found = new Set();
  collect(doc, segments, 0, found);
  if (found.size === 0) return [];

  const list = found.size === 1 ? [...found][0] : [...found];
  return (Array.isArray(list) ? list : [list]).filter(
    (value) => value !== undefined && value !== null && !isPlainObject(value)
  );
}

/**
 * The exact values of the keys of the rules, on example documents.
 * @param {object[]} docs - The example documents.
 * @param {object} [projection] - A Projection applied to the sample documents before rule selectors.
 * @returns {{valuesOf: function(string): string[]|undefined}} `valuesOf(key)`: the distinct values of the key
 *   (selectors and "[]" allowed), as strings in the order they are found; undefined for a key that cannot be read.
 */
export function createExactValues(docs, projection) {
  const plan = projectionView(projection) ? compilePlan(projection) : undefined;
  let projected;
  const cache = new Map();

  return {
    valuesOf(key) {
      if (typeof key !== "string") return undefined;
      if (cache.has(key)) return cache.get(key);

      const { segments } = parseFilterSegments(key.trim());
      if (!segments) {
        cache.set(key, undefined);
        return undefined;
      }

      projected ??= plan ? docs.map((doc) => applyPlan(doc, plan)) : docs;

      const distinct = new Set();
      for (const doc of projected) {
        for (const value of leafValues(doc, segments)) distinct.add(String(value));
      }

      const values = [...distinct];
      cache.set(key, values);
      return values;
    }
  };
}

/**
 * The values a rule can find for a key: the exact ones when there are example
 * documents, otherwise the flat values of the schema after the projection (the
 * caller computes them).
 * @param {{valuesOf: function}|undefined} exact - From createExactValues; undefined without documents.
 * @param {string} key - The key of the rule.
 * @param {function(): Array} flat - The flat values, only called without documents.
 * @returns {Array}
 */
export function ruleValues(exact, key, flat) {
  const values = exact?.valuesOf(key);
  return values !== undefined ? values : flat();
}

/**
 * Adds the documents of a parsed JSON file to the ones kept in memory, within
 * the limits: a file whose text does not fit in MAX_CHARS is not kept, the
 * documents beyond MAX_DOCUMENTS are not kept.
 * @param {{docs: object[], chars: number}} state - What is kept.
 * @param {*} json - The parsed file: a document or an array of documents.
 * @param {number} chars - The length of the JSON text of the file.
 * @returns {{state: {docs: object[], chars: number}, kept: number, dropped: number, reason?: "size"|"count"}}
 */
export function mergeDocuments(state, json, chars) {
  const list = (Array.isArray(json) ? json : [json]).filter(isPlainObject);
  if (list.length === 0) return { state, kept: 0, dropped: 0 };

  if (state.chars + chars > MAX_CHARS) {
    return { state, kept: 0, dropped: list.length, reason: "size" };
  }

  const room = Math.max(0, MAX_DOCUMENTS - state.docs.length);
  const kept = list.slice(0, room);
  const dropped = list.length - kept.length;
  if (kept.length === 0) return { state, kept: 0, dropped, reason: "count" };

  return {
    state: {
      docs: [...state.docs, ...kept],
      chars: state.chars + Math.round((chars * kept.length) / list.length)
    },
    kept: kept.length,
    dropped,
    ...(dropped > 0 && { reason: "count" })
  };
}

/**
 * Do the values of this pipeline depend on the correlation between fields, so
 * that the flat values of the schema are not exact? Yes with the filters of an
 * active projection, and with an active rule whose key has selectors.
 * @param {object} config - The pipeline config (as in the editor).
 * @returns {boolean}
 */
export function needsExactValues(config) {
  if (projectionView(config?.projection)?.selectors.length > 0) return true;

  return activeRuleKeys(config).some(({ key }) =>
    parseFilterSegments(key.trim()).segments?.some(({ selectors }) => selectors.length > 0)
  );
}
